"use client";

import type { ComponentProps, ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import {
  BrainIcon,
  CheckIcon,
  ChevronDownIcon,
  Loader2Icon,
} from "lucide-react";
import { createContext, memo, useContext, useMemo } from "react";

type ChainOfThoughtContextValue = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
};

const ChainOfThoughtContext = createContext<ChainOfThoughtContextValue | null>(
  null,
);

const useChainOfThought = () => {
  const context = useContext(ChainOfThoughtContext);
  if (!context) {
    throw new Error(
      "ChainOfThought components must be used within ChainOfThought",
    );
  }
  return context;
};

export type ChainOfThoughtProps = ComponentProps<"div"> & {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const ChainOfThought = memo(
  ({
    className,
    open,
    defaultOpen = false,
    onOpenChange,
    children,
    ...props
  }: ChainOfThoughtProps) => {
    const [isOpen, setIsOpen] = useControllableState({
      defaultProp: defaultOpen,
      onChange: onOpenChange,
      prop: open,
    });

    const chainOfThoughtContext = useMemo(
      () => ({ isOpen, setIsOpen }),
      [isOpen, setIsOpen],
    );

    return (
      <ChainOfThoughtContext.Provider value={chainOfThoughtContext}>
        <Collapsible open={isOpen} onOpenChange={setIsOpen} {...props}>
          <div className={cn("not-prose", className)}>{children}</div>
        </Collapsible>
      </ChainOfThoughtContext.Provider>
    );
  },
);

export type ChainOfThoughtHeaderProps = ComponentProps<
  typeof CollapsibleTrigger
> & {
  isLoading?: boolean;
};

export const ChainOfThoughtHeader = memo(
  ({ className, children, isLoading, ...props }: ChainOfThoughtHeaderProps) => {
    const { isOpen } = useChainOfThought();

    return (
      <CollapsibleTrigger
        className={cn(
          "flex gap-2 items-center w-full text-sm transition-colors cursor-pointer text-muted-foreground hover:text-foreground",
          className,
        )}
        {...props}
      >
        {isLoading ? (
          <Loader2Icon
            aria-hidden
            className="animate-spin size-4 shrink-0 text-majorelle"
          />
        ) : (
          <BrainIcon aria-hidden className="size-4 shrink-0 text-majorelle" />
        )}
        <span className="flex-1 text-left">
          {children ?? "Chain of Thought"}
        </span>
        <ChevronDownIcon
          className={cn(
            "transition-transform size-4 shrink-0",
            isOpen ? "rotate-180" : "rotate-0",
          )}
        />
      </CollapsibleTrigger>
    );
  },
);

export type ChainOfThoughtStepProps = ComponentProps<"div"> & {
  label: ReactNode;
  description?: ReactNode;
  status?: "complete" | "active" | "pending";
  isLast?: boolean;
};

const stepStatusStyles = {
  active: "text-foreground",
  complete: "text-muted-foreground",
  pending: "text-muted-foreground/50",
};

export const ChainOfThoughtStep = memo(
  ({
    className,
    label,
    description,
    status = "complete",
    isLast = false,
    children,
    ...props
  }: ChainOfThoughtStepProps) => (
    <div
      className={cn(
        "flex gap-2.5 text-sm",
        stepStatusStyles[status],
        "fade-in-0 slide-in-from-top-2 animate-in",
        className,
      )}
      {...props}
    >
      <div className="relative shrink-0">
        {status === "active" ? (
          <span className="flex justify-center items-center rounded-full size-5 bg-majorelle/10">
            <Loader2Icon className="animate-spin size-3.5 text-majorelle" />
          </span>
        ) : status === "complete" ? (
          <span className="flex justify-center items-center rounded-full size-5 bg-majorelle text-majorelle-foreground">
            <CheckIcon className="size-3" strokeWidth={3} />
          </span>
        ) : (
          <span className="block rounded-full border size-5 border-muted-foreground/30" />
        )}
        {!isLast && (
          <div className="absolute top-6 -bottom-5 left-1/2 w-px -translate-x-1/2 bg-border" />
        )}
      </div>
      <div className="overflow-hidden flex-1 pb-4 space-y-0.5">
        <div>{label}</div>
        {description && (
          <div className="text-xs text-muted-foreground">{description}</div>
        )}
        {children}
      </div>
    </div>
  ),
);

export type ChainOfThoughtSearchResultsProps = ComponentProps<"div">;

export const ChainOfThoughtSearchResults = memo(
  ({ className, ...props }: ChainOfThoughtSearchResultsProps) => (
    <div
      className={cn("mt-1.5 flex flex-wrap gap-1.5", className)}
      {...props}
    />
  ),
);

export type ChainOfThoughtSearchResultProps = ComponentProps<typeof Badge>;

export const ChainOfThoughtSearchResult = memo(
  ({ className, children, ...props }: ChainOfThoughtSearchResultProps) => (
    <Badge
      variant="secondary"
      className={cn("gap-1 text-xs font-normal", className)}
      {...props}
    >
      {children}
    </Badge>
  ),
);

export type ChainOfThoughtContentProps = ComponentProps<
  typeof CollapsibleContent
>;

export const ChainOfThoughtContent = memo(
  ({ className, children, ...props }: ChainOfThoughtContentProps) => (
    <CollapsibleContent
      className={cn(
        "overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down",
        className,
      )}
      {...props}
    >
      <div className="mt-3 space-y-5">{children}</div>
    </CollapsibleContent>
  ),
);

export type ChainOfThoughtImageProps = ComponentProps<"div"> & {
  caption?: string;
};

export const ChainOfThoughtImage = memo(
  ({ className, children, caption, ...props }: ChainOfThoughtImageProps) => (
    <div className={cn("mt-2 space-y-1.5", className)} {...props}>
      <div className="overflow-hidden rounded-md border">{children}</div>
      {caption && <p className="text-xs text-muted-foreground">{caption}</p>}
    </div>
  ),
);

ChainOfThought.displayName = "ChainOfThought";
ChainOfThoughtHeader.displayName = "ChainOfThoughtHeader";
ChainOfThoughtStep.displayName = "ChainOfThoughtStep";
ChainOfThoughtSearchResults.displayName = "ChainOfThoughtSearchResults";
ChainOfThoughtSearchResult.displayName = "ChainOfThoughtSearchResult";
ChainOfThoughtContent.displayName = "ChainOfThoughtContent";
ChainOfThoughtImage.displayName = "ChainOfThoughtImage";
